import { TmFormElement } from '@thiememeulenhoff/lit-sdk';
import { html, nothing, PropertyValues } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { styles, tag } from './feedback-text-input.styles';

@customElement(tag)
export class FeedbackTextInputComponent extends TmFormElement {
  static override styles = styles;

  @query('#inputElement')
  textarea!: HTMLTextAreaElement;

  @property({ type: String, reflect: true })
  override placeholder?: string;

  @property({ type: Boolean, reflect: true })
  loading = false;

  @property({ type: Number, reflect: true })
  maxlength = 500;

  #htmlPlaceholder(text: string) {
    if (!text) return text;

    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;

    return textarea.value;
  }

  override handleInput(event: InputEvent) {
    this.value = (event.target as HTMLTextAreaElement).value;

    this.#autoGrow();

    super.handleInput(event);
  }

  #autoGrow() {
    this.textarea.style.height = 'auto';

    // calculate up to 5 lines, and check the correct position for the icons
    const maxHeight =
      parseFloat(getComputedStyle(this.textarea).lineHeight) * 5 +
      parseFloat(getComputedStyle(this.textarea).paddingTop) +
      parseFloat(getComputedStyle(this.textarea).paddingBottom);

    if (this.textarea.scrollHeight > maxHeight) {
      this.textarea.style.height = `${maxHeight}px`;
      this.textarea.style.overflowY = 'auto';
    } else {
      this.textarea.style.height = `${this.textarea.scrollHeight}px`;
      this.textarea.style.overflowY = 'hidden';
    }
  }

  override firstUpdated() {
    this.#autoGrow();
  }

  override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    if (changedProperties.has('value')) {
      this.#autoGrow();
    }
  }

  override handleChange(event: Event) {
    super.handleChange(event);
  }

  #handleIconClick() {
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    this.dispatchLocalEvent({ eventName: 'icon-click' });
  }

  override render() {
    const htmlPlaceholder = this.#htmlPlaceholder(this.placeholder ?? '');

    return html`
      <div class="input-wrapper">
        <textarea
          id="inputElement"
          part="input"
          data-test="feedback-input"
          .placeholder=${htmlPlaceholder || nothing}
          ?readonly=${this.readonly}
          .value=${this.value ?? ''}
          ?disabled=${this.disabled}
          loading="${this.loading}"
          @input=${this.handleInput}
          @change=${this.handleChange}
          maxlength=${this.maxlength}
          rows="1"
        ></textarea>
        ${this.loading
          ? html`<a-loader></a-loader>`
          : html`
              <a-icon
                name="send"
                class="icon"
                @click=${this.#handleIconClick}
              ></a-icon>
            `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-feedback-text-input': FeedbackTextInputComponent;
  }
}
